/**
 * Os horários de um esquema — **o buraco que impedia tudo**.
 *
 * Sem eles não existe onde pôr aula, e a conferência recusa o projeto inteiro
 * com "nenhum esquema tem horários definidos". A rota existia e era testada
 * desde a Fase 0; a tela, não. A ESPM só tem horários porque um script os
 * criou.
 *
 * **Por receita, e não um a um.** Oito tempos × cinco dias são quarenta
 * cadastros idênticos, e é o tipo de trabalho que faz a pessoa desistir antes
 * de chegar na grade. O assistente — "quantos tempos, de quanto, a partir de
 * que hora, em que dias, com recreio depois de qual" — é o que o concorrente
 * tem e o que torna isto operável numa tarde.
 *
 * O cálculo mora no servidor (`gerarSlots`), e não aqui: a mesma receita
 * precisa valer para quem chamar a API direto, e duas implementações da
 * aritmética de intervalo divergiriam no primeiro recreio.
 */

import { useCallback, useEffect, useState } from 'react'

import { ErroApi, api, type Linha } from '../api'
import { Icon } from '../components/Icon'

const DIAS = [
  { n: 1, nome: 'Segunda' },
  { n: 2, nome: 'Terça' },
  { n: 3, nome: 'Quarta' },
  { n: 4, nome: 'Quinta' },
  { n: 5, nome: 'Sexta' },
  { n: 6, nome: 'Sábado' },
  { n: 7, nome: 'Domingo' },
]

/** `450` → `"07:30"`. O banco guarda minuto do dia; a pessoa lê hora. */
const hhmm = (minutos: number) =>
  `${String(Math.floor(minutos / 60)).padStart(2, '0')}:${String(minutos % 60).padStart(2, '0')}`

export function Horarios({ projeto, esquema }: { projeto: string; esquema: string }) {
  const [itens, setItens] = useState<Linha[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const [dias, setDias] = useState<number[]>([1, 2, 3, 4, 5])
  const [inicio, setInicio] = useState('07:30')
  const [duracao, setDuracao] = useState(50)
  const [aulas, setAulas] = useState(5)
  const [temRecreio, setTemRecreio] = useState(false)
  const [recreioApos, setRecreioApos] = useState(3)
  const [recreioMin, setRecreioMin] = useState(20)

  const carregar = useCallback(async () => {
    try {
      setItens(await api.slots(projeto, esquema))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar')
    }
  }, [projeto, esquema])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function gerar() {
    setOcupado(true)
    setErro(null)
    try {
      setItens(
        await api.gerarSlots(projeto, esquema, {
          dias,
          inicio,
          duracao_min: duracao,
          aulas,
          intervalo_apos: temRecreio ? recreioApos : null,
          intervalo_min: recreioMin,
        }),
      )
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível gerar')
    } finally {
      setOcupado(false)
    }
  }

  const porDia = new Map<number, Linha[]>()
  for (const s of itens) {
    const d = Number(s.dia)
    porDia.set(d, [...(porDia.get(d) ?? []), s])
  }
  const aulasPorDia = [...porDia.values()][0]?.filter((s) => s.tipo === 'AULA').length ?? 0

  return (
    // `data-tour` é o alvo que o manual aponta. Fica na seção inteira
    // porque o que o passo descreve é o bloco, e não um campo dele.
    <section className="section" data-tour="horarios">
      <div className="section__head">
        <h2>Horários deste esquema</h2>
        <span className="field__hint">
          {itens.length === 0
            ? 'nenhum ainda'
            : `${itens.filter((s) => s.tipo === 'AULA').length} horários, ${aulasPorDia} por dia`}
        </span>
      </div>

      <p className="callout">
        <strong>Sem horários, nenhuma grade fecha.</strong>
        <span>
          {' '}
          É o primeiro impedimento que a conferência aponta. Gerar substitui os horários que
          estiverem aqui — e as aulas já alocadas neles são perdidas, então gere antes de montar a
          grade, não depois.
        </span>
      </p>

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      <div className="form__grade">
        <div className="field">
          <span className="field__label">Em que dias</span>
          <div className="row horarios__dias">
            {DIAS.map((d) => (
              <label className="checkbox" key={d.n}>
                <input
                  type="checkbox"
                  checked={dias.includes(d.n)}
                  onChange={(e) =>
                    setDias((atual) =>
                      e.target.checked
                        ? [...atual, d.n].sort((a, b) => a - b)
                        : atual.filter((x) => x !== d.n),
                    )
                  }
                />
                {d.nome}
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="horarios-inicio">
            Primeiro horário começa às
          </label>
          <input
            id="horarios-inicio"
            className="input input--mono"
            type="time"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="horarios-aulas">
            Quantos por dia
          </label>
          <input
            id="horarios-aulas"
            className="input input--mono"
            type="number"
            min={1}
            value={aulas}
            onChange={(e) => setAulas(Number(e.target.value))}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="horarios-duracao">
            Minutos cada
          </label>
          <input
            id="horarios-duracao"
            className="input input--mono"
            type="number"
            min={1}
            value={duracao}
            onChange={(e) => setDuracao(Number(e.target.value))}
          />
          <span className="field__hint">
            Tempo de 1h40 são duas aulas de 50 — conte em aulas, não em tempos.
          </span>
        </div>

        <div className="field">
          <span className="field__label">Recreio</span>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={temRecreio}
              onChange={(e) => setTemRecreio(e.target.checked)}
            />
            <span>Tem recreio</span>
          </label>
        </div>

        {temRecreio && (
          <>
            <div className="field">
              <label className="field__label" htmlFor="horarios-apos">
                Depois de qual
              </label>
              <input
                id="horarios-apos"
                className="input input--mono"
                type="number"
                min={1}
                value={recreioApos}
                onChange={(e) => setRecreioApos(Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="horarios-recreio">
                De quantos minutos
              </label>
              <input
                id="horarios-recreio"
                className="input input--mono"
                type="number"
                min={1}
                value={recreioMin}
                onChange={(e) => setRecreioMin(Number(e.target.value))}
              />
            </div>
          </>
        )}
      </div>

      <div className="row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={ocupado || dias.length === 0}
          onClick={() => void gerar()}
        >
          {ocupado ? 'Gerando…' : itens.length > 0 ? 'Gerar de novo' : 'Gerar horários'}
        </button>
        {dias.length === 0 && <span className="field__hint">Escolha ao menos um dia.</span>}
      </div>

      {itens.length > 0 && (
        // Um dia só, e não os cinco: a receita gera todos iguais, e repetir a
        // mesma coluna cinco vezes não acrescenta informação nenhuma.
        <div className="tabela-rolavel">
          <table className="tabela">
            <thead>
              <tr>
                <th>Ordem</th>
                <th>Começa</th>
                <th>Termina</th>
                <th>O que é</th>
              </tr>
            </thead>
            <tbody>
              {(porDia.get([...porDia.keys()].sort((a, b) => a - b)[0] ?? 1) ?? []).map((s) => (
                <tr key={String(s.id)}>
                  <td className="mono">{String(Number(s.ordem) + 1)}</td>
                  <td className="mono">{hhmm(Number(s.inicio))}</td>
                  <td className="mono">{hhmm(Number(s.inicio) + Number(s.duracao_min))}</td>
                  <td>{s.tipo === 'AULA' ? 'Aula' : 'Intervalo'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
